import {
    useEffect,
    useRef,
    useState
} from 'react'
import { useFormContext } from 'react-hook-form'

import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import SignaturePad from 'react-signature-canvas'

function dataURLtoFile(dataurl) {
    const arr = dataurl.split(',')
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
    }

    return new File([u8arr], 'signature.png', { type: 'image/png' })
}

export default function Signature({
    label,
    name
}) {
    const padRef = useRef(null)
    const {
        setValue,
        watch
    } = useFormContext()
    const signature = watch(name)
    const [isLoadingImage, setIsLoadingImage] = useState(false)

    const handleSet = () => {
        const file = dataURLtoFile(padRef.current.toDataURL('image/png'))

        Object.defineProperty(file, '_id', {
            value: Date.now(),
            enumerable: true,
        })

        setValue(
            name,
            file,
            { shouldDirty: true }
        )
    }

    const handleReset = () => {
        padRef.current.clear()
        setValue(name, '')
        setIsLoadingImage(false)
    }

    useEffect(() => {
        if (!padRef.current || !signature) {
            setIsLoadingImage(false)
            return
        }

        setIsLoadingImage(true)

        const canvas = padRef.current.getCanvas()
        const ctx = canvas.getContext('2d')

        const img = new Image()
        img.crossOrigin = 'anonymous'

        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(
                img,
                0,
                0,
                canvas.width,
                canvas.height
            )

            setIsLoadingImage(false)
        }

        img.onerror = () => {
            setIsLoadingImage(false)
        }

        if (typeof signature === 'string') {
            const isDataUrl =
                signature.startsWith('data:') ||
                signature.startsWith('blob:')

            img.src = isDataUrl
                ? signature
                : `${signature}?cb=${Date.now()}`

        } else if (
            signature instanceof File ||
            signature instanceof Blob
        ) {
            const objectUrl = URL.createObjectURL(signature)

            img.src = objectUrl

            return () => {
                URL.revokeObjectURL(objectUrl)
            }
        }
    }, [signature])

    return (
        <div
            style={{
                width: '100%',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    mb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Typography variant='body2'>
                    {label}
                </Typography>

                <IconButton
                    size='small'
                    onClick={handleReset}
                >
                    <RefreshIcon />
                </IconButton>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    height: 100,
                    overflow: 'hidden',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    position: 'relative'
                }}
            >
                {isLoadingImage && (
                    <Skeleton
                        variant='rectangular'
                        animation='wave'
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 2,
                        }}
                    />
                )}

                <SignaturePad
                    ref={padRef}
                    onEnd={handleSet}
                    canvasProps={{
                        width: 390,
                        height: 100,
                        style: {
                            width: '100%',
                            height: '100%',
                        }
                    }}
                    clearOnResize={false}
                    minWidth={1.5}
                    maxWidth={4}
                />
            </Box>
        </div>
    )
}